import { SessionType, type SessionTypeKey } from '@/stores/sessionType'

/**
 * Ordered list of session type keys from highest tier to lowest.
 * Used for upgrade/downgrade path resolution and rank comparisons.
 */
export const SESSION_TYPE_ORDER: SessionTypeKey[] = [
  SessionType.EliteUnlimited,
  SessionType.ElitePlus,
  SessionType.Elite,
  SessionType.MostPopular,
  SessionType.Legacy,
  SessionType.LegacyRoadshowPlus,
  SessionType.Standard,
  SessionType.StandardRoadshowPlus,
  SessionType.Simple,
  SessionType.SimpleRoadshowPlus,
  SessionType.BasicMax,
  SessionType.BasicMaxRoadshowPlus,
  SessionType.BasicPlus,
  SessionType.BasicPlusRoadshowPlus,
  SessionType.Basic,
  SessionType.BasicRoadshowPlus,
  // MiniRoadshowPlus and EssentialRoadshowPlus are included here defensively so
  // getSessionTypeRank returns a defined index. Their standard siblings (Mini/Essential)
  // remain off-ladder for tier-rank purposes, but DO have explicit entries in the
  // upgrade maps in `./upgrades.ts` — Mini upgrades like Simple (→ Standard) and
  // Essential upgrades like Standard (→ Legacy) per the scene-selection guide
  // (`docs/implementation-guides/2026.03.12-005-scene-selection.md`).
  SessionType.MiniRoadshowPlus,
  SessionType.EssentialRoadshowPlus
]

/**
 * Returns the tier rank index for a session type key (0 = highest).
 * Returns -1 if the key is not found in the order list.
 */
export function getSessionTypeRank(key: SessionTypeKey): number {
  return SESSION_TYPE_ORDER.indexOf(key)
}

/**
 * Returns true if `candidateKey` is a higher tier than `currentKey`.
 */
export function isUpgrade(currentKey: SessionTypeKey, candidateKey: SessionTypeKey): boolean {
  const currentRank = getSessionTypeRank(currentKey)
  const candidateRank = getSessionTypeRank(candidateKey)
  if (currentRank === -1 || candidateRank === -1) return false
  return candidateRank < currentRank
}
